import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import TenantSelector from '#/common/components/TenantSelector';
import { ImportFileOutputData, Tenant } from '#/graphql/types';
import useSession from '#/common/hooks/useSession';
import { OptionInfo } from '#/common/components/ColumnMapper';
import useToast from '#/common/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import ImportWizardBase, { WizardStep } from '../components/base/ImportWizardBase';
import FileUploadStep from '../components/base/FileUploadStep';

// Number of data rows (excluding the header) kept for the wizard's Preview step.
const PREVIEW_DATA_ROW_COUNT = 10;

const CSV_PARSE_ERROR_MESSAGE =
  'Unable to parse this CSV file. Please check that it is a valid CSV (matching quotes, consistent columns) and try again.';

export interface MandatoryStepProps {
  csvColumns: OptionInfo[];
  mapping: Record<string, string>;
  setMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  targetDropdownSelections: Record<string, string>;
  setTargetDropdownSelections: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface AdditionalStepProps {
  csvColumns: OptionInfo[];
  mapping: Record<string, string>;
  setMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export interface ImportPreviewStepProps {
  mapping: Record<string, string>;
  csvTopLines: string[][];
}

export interface ImportSubmitModalProps {
  visible: boolean;
  uploadedFile: File | null;
  mapping: Record<string, string>;
  uniqueField: string;
  selectedTenant?: Tenant;
  onHiding: () => void;
  onImportStart: () => void;
  onSuccess: (importResult: ImportFileOutputData) => void;
  onFailed: (error: unknown) => void;
}

export interface ImportSuccessModalProps {
  visible: boolean;
  onHiding: () => void;
  importResult: ImportFileOutputData | null;
}

export interface ImportWizardPageProps {
  /** Page title, shown in the SubPage header, e.g. "New Data Asset Import". */
  title: string;
  /** Lowercase plural entity name used in toasts and error messages, e.g. "assets". */
  entityLabelPlural: string;
  /** Route to redirect to once the import finished with no errors, e.g. "/assets/list". */
  listPath: string;
  /** Initial state for the dropdown selections used to hide options already selected. */
  initialTargetDropdownSelections: Record<string, string>;
  /** Key into targetDropdownSelections whose value is passed as `uniqueField` to the SubmitModal. */
  uniqueFieldKey: string;
  MapMandatoryFieldsStep: React.ComponentType<MandatoryStepProps>;
  MapAdditionalFieldsStep: React.ComponentType<AdditionalStepProps>;
  PreviewStep: React.ComponentType<ImportPreviewStepProps>;
  SubmitModal: React.ComponentType<ImportSubmitModalProps>;
  SuccessModal: React.ComponentType<ImportSuccessModalProps>;
}

const ImportWizardPage: React.FC<ImportWizardPageProps> = ({
  title,
  entityLabelPlural,
  listPath,
  initialTargetDropdownSelections,
  uniqueFieldKey,
  MapMandatoryFieldsStep,
  MapAdditionalFieldsStep,
  PreviewStep,
  SubmitModal,
  SuccessModal,
}) => {
  const { alert } = useToast();
  const navigate = useNavigate();
  const { tenants: tenantsData } = useSession();
  const tenants = tenantsData?.map((t) => t.tenant) ?? [];
  const [selectedTenant, setSelectedTenant] = useState<Tenant | undefined>(
    tenants.length === 1 ? tenants[0] : undefined,
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [insertingNewRecords, setInsertingNewRecords] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [importResult, setImportResult] = useState<ImportFileOutputData | null>(null);

  const [isStepMapMandatoryValid, setIsStepMapMandatoryValid] = useState<boolean>(false);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [csvColumns, setCsvColumns] = useState<OptionInfo[]>([]);
  const [csvTopLines, setCsvTopLines] = useState<string[][]>([]);

  // Save the state of dropdown selections to hide options that are already selected
  const [targetDropdownSelections, setTargetDropdownSelections] = useState<Record<string, string>>(
    initialTargetDropdownSelections,
  );

  const handleFileSelect = useCallback(
    (file: File | null) => {
      setUploadedFile(file);
      // Reset states when a new file is uploaded
      setIsStepMapMandatoryValid(false);
      setMapping({});
      setCsvColumns([]);
      setCsvTopLines([]);
      setTargetDropdownSelections(initialTargetDropdownSelections);
    },
    [initialTargetDropdownSelections],
  );

  const handleFileDelete = useCallback(() => {
    handleFileSelect(null);
  }, [handleFileSelect]);

  // Parse the CSV file to extract the header row + Preview rows using PapaParse's
  // real CSV state machine (handles quoted commas, escaped quotes, embedded newlines,
  // UTF-8 BOM, and CRLF/LF
  // line endings) instead of naive per-line comma splitting.
  // `preview` stops PapaParse from reading past the rows the wizard actually needs,
  // so we don't have to parse/hold the entire file just to build headers + Preview.
  useEffect(() => {
    if (!uploadedFile) return;

    // Guards against a stale parse (from a previously selected file) completing
    // after a newer file has already been selected and overwriting its results.
    let stale = false;

    Papa.parse<string[]>(uploadedFile, {
      preview: PREVIEW_DATA_ROW_COUNT + 1, // header row + N data rows
      skipEmptyLines: true,
      transform: (value) => value.trim(),
      complete: (results) => {
        if (stale) return;

        if (results.errors.length > 0) {
          console.error('Failed to parse CSV file for import preview', results.errors);
          setCsvColumns([]);
          setCsvTopLines([]);
          void alert('error', CSV_PARSE_ERROR_MESSAGE);
          return;
        }

        const rows = results.data;
        if (rows.length === 0) {
          setCsvColumns([]);
          setCsvTopLines([]);
          return;
        }

        // Generate column info with letters (A, B, C, etc.) and titles
        const columns: OptionInfo[] = rows[0].map((title, index) => {
          const letter = String.fromCodePoint(65 + index); // 65 is 'A' in ASCII
          return {
            key: title,
            label: title ? `Column ${letter} (${title})` : `Column ${letter}`,
          };
        });

        setCsvColumns(columns);
        setCsvTopLines(rows);
      },
      error: (error) => {
        if (stale) return;
        console.error('Failed to parse CSV file for import preview', error);
        setCsvColumns([]);
        setCsvTopLines([]);
        void alert('error', CSV_PARSE_ERROR_MESSAGE);
      },
    });

    return () => {
      stale = true;
    };
  }, [uploadedFile, alert]);

  const onSubmitSuccess = useCallback(
    (result: ImportFileOutputData) => {
      setImportResult(result);
      setInsertingNewRecords(false);

      if (result.totalRows === result.successfulImports) {
        void alert(
          'success',
          `${result.successfulImports ?? 0} ${entityLabelPlural} were imported successfully!`,
        );
        navigate(listPath);
      } else {
        setShowSuccessModal(true);
      }
    },
    [alert, navigate, listPath, entityLabelPlural],
  );

  const onSubmitFailed = useCallback(
    (error: unknown) => {
      setImportResult(null);
      setInsertingNewRecords(false);

      console.error(`Failed to import ${entityLabelPlural}`, error);
      const errorMsg = [`Failed to import ${entityLabelPlural}!`];
      if (typeof error === 'string') {
        errorMsg.push(error);
      }
      if (error instanceof Error) {
        errorMsg.push(error.message);
      }
      void alert('error', errorMsg.join(' '));
    },
    [alert, entityLabelPlural],
  );

  const onSuccessModalHiding = () => {
    setShowSuccessModal(false);
    setImportResult(null);
  };

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        component: (
          <FileUploadStep
            uploadedFile={uploadedFile}
            handleFileSelect={handleFileSelect}
            handleFileDelete={handleFileDelete}
          />
        ),
        isValid: !!selectedTenant && !!uploadedFile,
      },
      {
        component: (
          <MapMandatoryFieldsStep
            csvColumns={csvColumns}
            mapping={mapping}
            setMapping={setMapping}
            targetDropdownSelections={targetDropdownSelections}
            setTargetDropdownSelections={setTargetDropdownSelections}
            setIsValid={setIsStepMapMandatoryValid}
          />
        ),
        isValid: isStepMapMandatoryValid,
      },
      {
        component: (
          <MapAdditionalFieldsStep
            csvColumns={csvColumns}
            mapping={mapping}
            setMapping={setMapping}
          />
        ),
      },
      {
        component: <PreviewStep mapping={mapping} csvTopLines={csvTopLines} />,
      },
    ],
    [
      uploadedFile,
      handleFileSelect,
      selectedTenant,
      MapMandatoryFieldsStep,
      csvColumns,
      mapping,
      targetDropdownSelections,
      isStepMapMandatoryValid,
      MapAdditionalFieldsStep,
      PreviewStep,
      csvTopLines,
    ],
  );

  return tenants.length > 1 && !selectedTenant ? (
    <TenantSelector onTenantSelect={setSelectedTenant} />
  ) : (
    <ImportWizardBase
      title={title}
      steps={steps}
      onSubmit={() => setShowSubmitModal(true)}
      insertingRecords={insertingNewRecords}
      modals={
        <>
          <SubmitModal
            visible={showSubmitModal}
            uploadedFile={uploadedFile}
            mapping={mapping}
            uniqueField={targetDropdownSelections[uniqueFieldKey]}
            selectedTenant={selectedTenant}
            onHiding={() => setShowSubmitModal(false)}
            onImportStart={() => setInsertingNewRecords(true)}
            onSuccess={onSubmitSuccess}
            onFailed={onSubmitFailed}
          />
          <SuccessModal
            visible={showSuccessModal}
            onHiding={onSuccessModalHiding}
            importResult={importResult}
          />
        </>
      }
    />
  );
};

export default React.memo(ImportWizardPage);
